import Link from '@/components/compat/Link';
import { ArrowLeft, Plus, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useCms } from '@/components/painel/cms/cms-context';
import { usePainelText } from '@/components/painel/painel-texts-context';
import { painelInputClass, painelSelectTriggerClass, painelTextareaClass } from '@/components/painel/cms/painel-form';
import {
  painelSurfaceClass,
  painelButtonOutlineClass,
  painelSelectContentClass,
} from '@/components/painel/painel-styles';
import {
  STAFF_JOIN_STEP_ICON_OPTIONS,
  STAFF_ROLE_ORDER,
  STAFF_SECTION_LABEL,
} from '@/components/help-center/equipe-shared';
import type { SiteStaffJoinStepIcon, SiteStaffRole } from '@/lib/cms-types';
import { normalizeStaffJoinSection, normalizeStaffLearnMore } from '@/lib/site-defaults';

export function PainelEquipeTextsEditorPage() {
  const pageText = usePainelText('pagina_equipe_textos');
  const { site, setSite, hydrated } = useCms();
  const join = site.staffJoinSection;
  const learnMore = site.staffLearnMore;

  const setRoleDescription = (role: SiteStaffRole, value: string) => {
    setSite({
      ...site,
      staffRoleDescriptions: { ...site.staffRoleDescriptions, [role]: value },
    });
  };

  const patchJoin = (patch: Partial<typeof join>) => {
    setSite({
      ...site,
      staffJoinSection: normalizeStaffJoinSection({ ...join, ...patch }),
    });
  };

  const patchLearnMore = (patch: Partial<typeof learnMore>) => {
    setSite({
      ...site,
      staffLearnMore: normalizeStaffLearnMore({ ...learnMore, ...patch }),
    });
  };

  const updateStep = (
    index: number,
    patch: Partial<(typeof join.steps)[number]>
  ) => {
    const steps = join.steps.map((step, i) => (i === index ? { ...step, ...patch } : step));
    patchJoin({ steps });
  };

  const addStep = () => {
    patchJoin({
      steps: [
        ...join.steps,
        {
          id: crypto.randomUUID(),
          title: 'Novo passo',
          text: '',
          icon: 'Compass' as SiteStaffJoinStepIcon,
        },
      ],
    });
  };

  const removeStep = (index: number) => {
    if (join.steps.length <= 1) return;
    patchJoin({ steps: join.steps.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-8">
      <div>
        <Button
          variant="ghost"
          asChild
          className="mb-2 -ml-2 rounded-xl text-slate-300 hover:bg-white/10 hover:text-white"
        >
          <Link href="/painel/paginas">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Páginas
          </Link>
        </Button>
        <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
          {pageText.title}
        </h1>
        <p className="mt-1 font-inter text-sm text-slate-400">{pageText.description}</p>
      </div>

      <div className={cn(painelSurfaceClass, 'space-y-5 p-6 md:p-8', !hydrated && 'opacity-70')}>
        <div className="grid gap-2">
          <Label htmlFor="equipe-title" className="text-blue-100/90">
            Título do hero
          </Label>
          <input
            id="equipe-title"
            value={site.staffPageTitle}
            onChange={(e) => setSite({ ...site, staffPageTitle: e.target.value })}
            className={painelInputClass}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="equipe-subtitle" className="text-blue-100/90">
            Subtítulo do hero
          </Label>
          <textarea
            id="equipe-subtitle"
            value={site.staffPageSubtitle}
            onChange={(e) => setSite({ ...site, staffPageSubtitle: e.target.value })}
            className={cn(painelInputClass, 'min-h-[80px] resize-y py-2')}
          />
        </div>
      </div>

      <div className={cn(painelSurfaceClass, 'space-y-5 p-6 md:p-8', !hydrated && 'opacity-70')}>
        <div>
          <h2 className="font-poppins text-lg font-bold text-white">Descrições por cargo</h2>
          <p className="mt-0.5 font-inter text-sm text-slate-400">
            Use uma linha em branco entre parágrafos. Cada bloco vira um item na caixa do cargo.
          </p>
        </div>
        {STAFF_ROLE_ORDER.map((role) => (
          <div key={role} className="grid gap-2">
            <Label htmlFor={`equipe-desc-${role}`} className="text-blue-100/90">
              {STAFF_SECTION_LABEL[role]}
            </Label>
            <textarea
              id={`equipe-desc-${role}`}
              value={site.staffRoleDescriptions[role] ?? ''}
              onChange={(e) => setRoleDescription(role, e.target.value)}
              className={cn(painelTextareaClass, 'min-h-[110px]')}
              placeholder={'Primeiro parágrafo…\n\nSegundo parágrafo…'}
            />
          </div>
        ))}
      </div>

      <div className={cn(painelSurfaceClass, 'space-y-5 p-6 md:p-8', !hydrated && 'opacity-70')}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-poppins text-lg font-bold text-white">
              Como se tornar membro da equipe
            </h2>
            <p className="mt-0.5 font-inter text-sm text-slate-400">
              Título, descrição e passos da jornada em /equipe.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-xl border-white/15 text-blue-100"
            onClick={addStep}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Passo
          </Button>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="grid gap-2">
            <Label className="text-blue-100/90">Badge</Label>
            <input
              value={join.badgeLabel}
              onChange={(e) => patchJoin({ badgeLabel: e.target.value })}
              className={painelInputClass}
            />
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label className="text-blue-100/90">Título</Label>
            <input
              value={join.title}
              onChange={(e) => patchJoin({ title: e.target.value })}
              className={painelInputClass}
            />
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label className="text-blue-100/90">Descrição</Label>
            <textarea
              value={join.subtitle}
              onChange={(e) => patchJoin({ subtitle: e.target.value })}
              className={cn(painelInputClass, 'min-h-[80px] resize-y py-2')}
            />
          </div>
        </div>

        <div className="space-y-4">
          {join.steps.map((step, index) => (
            <div
              key={step.id}
              className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-4"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="font-poppins text-sm font-semibold text-white">
                  Passo {String(index + 1).padStart(2, '0')}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-rose-300"
                  disabled={join.steps.length <= 1}
                  onClick={() => removeStep(index)}
                >
                  <Trash2 className="mr-1 h-3.5 w-3.5" />
                  Remover
                </Button>
              </div>
              <div className="grid gap-3 md:grid-cols-[180px_1fr]">
                <div className="grid gap-2">
                  <Label className="text-blue-100/90">Ícone</Label>
                  <Select
                    value={step.icon}
                    onValueChange={(value) =>
                      updateStep(index, { icon: value as SiteStaffJoinStepIcon })
                    }
                  >
                    <SelectTrigger className={painelSelectTriggerClass}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={painelSelectContentClass}>
                      {STAFF_JOIN_STEP_ICON_OPTIONS.map((icon) => (
                        <SelectItem key={icon} value={icon}>
                          {icon}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label className="text-blue-100/90">Título</Label>
                  <input
                    value={step.title}
                    onChange={(e) => updateStep(index, { title: e.target.value })}
                    className={painelInputClass}
                  />
                </div>
                <div className="grid gap-2 md:col-span-2">
                  <Label className="text-blue-100/90">Texto</Label>
                  <textarea
                    value={step.text}
                    onChange={(e) => updateStep(index, { text: e.target.value })}
                    className={cn(painelTextareaClass, 'min-h-[88px]')}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={cn(painelSurfaceClass, 'space-y-5 p-6 md:p-8', !hydrated && 'opacity-70')}>
        <div>
          <h2 className="font-poppins text-lg font-bold text-white">Quer saber mais?</h2>
          <p className="mt-0.5 font-inter text-sm text-slate-400">
            CTA final da página /equipe com links para regras e conduta.
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="grid gap-2 md:col-span-2">
            <Label className="text-blue-100/90">Título</Label>
            <input
              value={learnMore.title}
              onChange={(e) => patchLearnMore({ title: e.target.value })}
              className={painelInputClass}
            />
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label className="text-blue-100/90">Descrição</Label>
            <textarea
              value={learnMore.description}
              onChange={(e) => patchLearnMore({ description: e.target.value })}
              className={cn(painelInputClass, 'min-h-[80px] resize-y py-2')}
            />
          </div>
          <div className="grid gap-2">
            <Label className="text-blue-100/90">Botão Regras</Label>
            <input
              value={learnMore.regrasButtonLabel}
              onChange={(e) => patchLearnMore({ regrasButtonLabel: e.target.value })}
              className={painelInputClass}
            />
          </div>
          <div className="grid gap-2">
            <Label className="text-blue-100/90">Botão Conduta</Label>
            <input
              value={learnMore.condutaButtonLabel}
              onChange={(e) => patchLearnMore({ condutaButtonLabel: e.target.value })}
              className={painelInputClass}
            />
          </div>
        </div>
        <p className="font-inter text-xs text-slate-400/80">
          As alterações são gravadas automaticamente ao editar.
        </p>
      </div>

      <div className={cn(painelSurfaceClass, 'flex flex-wrap items-center justify-between gap-3 p-5')}>
        <div>
          <p className="font-poppins text-sm font-semibold text-white">Membros da equipe</p>
          <p className="font-inter text-xs text-slate-400">
            Adicionar, editar e organizar membros e cargos.
          </p>
        </div>
        <Button asChild variant="ghost" className={painelButtonOutlineClass}>
          <Link href="/painel/equipe">
            <Users className="mr-2 h-4 w-4" />
            Gerir membros
          </Link>
        </Button>
      </div>
    </div>
  );
}
